import { Component, computed, inject, input, output, signal, viewChild } from '@angular/core';
import { FileUpload, FileUploadEvent, FileUploadModule } from '@openng/optimus-ui/fileupload';
import { UploadFileInput } from '@it2go/types';
import { FileUploadButtonStore } from './file-upload-button.store';
import { TranslatePipe } from '@ngx-translate/core';
import { getFileNameWithSuffix, uploadFile } from '../../../utils/files.utils';
import { FileUploadProgress, FileUploadProgressStatus } from './file-upload-button.types';
import { IconComponent } from '../icon/icon.component';
import { SeverityType } from '../button/button.types';
import { UrlInput } from '@libs/types/src/common/input-type/url.input';

@Component({
    selector: 'app-file-upload-button',
    templateUrl: 'file-upload-button.component.html',
    styleUrls: ['file-upload-button.component.scss'],
    providers: [FileUploadButtonStore],
    imports: [
        FileUploadModule,
        TranslatePipe,
        IconComponent,
    ],
})
export class FileUploadButtonComponent {

    public readonly store = inject(FileUploadButtonStore);

    public acceptTypes = input<string>();
    public label = input<string>('common.upload');
    public icon = input<string>('pi-upload');
    public size = input<'small' | 'large' | undefined>(undefined);
    public severity = input<SeverityType>('primary');
    public filename = input<string>();
    public disabled = input<boolean>(false);

    public completedCallback = input<CallableFunction | undefined>();

    public progress = output<FileUploadProgress>();
    protected inProgress = signal<boolean>(false);

    protected upload = viewChild<FileUpload>(FileUpload);

    protected choseStyleClassSignal = computed(() => {
        const size = this.size() === 'small' ? 'p-button-sm' : this.size() === 'large' ? 'p-button-lg' : 'p-button';

        return this.label() ? `${size} p-button-${this.severity()}` : `${size} p-button-${this.severity()} p-button-icon-only`;
    });
    protected iconSignal = computed(() => this.inProgress() ? 'pi-spin pi-spinner' : this.icon());

    async onUpload(param: FileUploadEvent) {
        const filename = getFileNameWithSuffix(this.filename(), param.files[0].name);

        const uploadFileObj = await this.store.api.getPresignedUrl(<UploadFileInput>{
            name: filename,
            owner: null,
            replace: null,
            date: new Date(),
            accesses: null,
        });

        try {
            this.inProgress.set(true);
            this.progress.emit(<FileUploadProgress>{
                filename,
                status: FileUploadProgressStatus.STARTED,
            });

            this.progress.emit(<FileUploadProgress>{
                filename,
                percent: 0,
                status: FileUploadProgressStatus.UPLOADING,
            });
            await uploadFile({
                url: uploadFileObj.url,
                file: param.files[0],
            });

            if (this.completedCallback()) {
                this.progress.emit(<FileUploadProgress>{
                    filename,
                    percent: 0,
                    status: FileUploadProgressStatus.CALLING_CALLBACK,
                });
                await this.completedCallback()!(<UrlInput>{
                    url: uploadFileObj.path,
                    fileName: filename,
                }).then((res: any) => {
                    if (res && res.ok) {
                        this.progress.emit(<FileUploadProgress>{
                            filename,
                            url: uploadFileObj.path,
                            percent: 100,
                            status: FileUploadProgressStatus.COMPLETED_CALLBACK,
                        });
                    } else {
                        this.progress.emit(<FileUploadProgress>{
                            filename,
                            url: uploadFileObj.path,
                            status: FileUploadProgressStatus.FAILED,
                        });
                    }
                });
            } else {
                this.progress.emit(<FileUploadProgress>{
                    filename,
                    url: uploadFileObj.path,
                    percent: 100,
                    status: FileUploadProgressStatus.COMPLETED,
                });
            }
        } catch (error) {
            this.progress.emit(<FileUploadProgress>{
                filename,
                url: uploadFileObj.path,
                status: FileUploadProgressStatus.FAILED,
                error: error,
            });
        } finally {
            this.inProgress.set(false);
        }

        this.upload()?.clear();
    }
}
